import { useEffect, useState, useRef } from 'react';
import {
  Card,
  Typography,
  IconButton,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

import RefreshIcon from '@mui/icons-material/Refresh';
import moment from 'moment';
import { format } from 'date-fns';
import useLogs from '../useLogs';
import SimpleToolbar from '../../../components/SimpleToolbar';
import LogsFilter from '../LogsFilter';
import { finalDateRange } from '../service.js';

const DEFAULT_DATE_RANGE = [
  {
    startDate: moment().subtract(3, 'days').toDate(),
    endDate: new Date(),
    key: 'selection',
  },
];

function LogsTable() {
  const { getLogs } = useLogs();
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(DEFAULT_DATE_RANGE);
  const [levelFilter, setLevelFilter] = useState('');
  const [postbackIdFilter, setPostbackIdFilter] = useState('');
  const [clkidFilter, setClkidFilter] = useState('');
  const [finalLogs, setfinalLogs] = useState([]);
  const [modal, setModal] = useState({ open: false, title: '', content: '' });
  const isInitialMount = useRef(true);

  const fetchLogs = (range, level, postbackId, clkid) => {
    setLoading(true);
    const apiCall = getLogs();

    const filters = {
      startDate: range[0].startDate,
      endDate: range[0].endDate,
    };
    if (level) filters.level = level;
    if (postbackId) filters.id = postbackId;
    if (clkid) filters.clkid = clkid;

    (async function () {
      try {
        const logs = await apiCall.call(filters);
        setfinalLogs(logs);
      } catch (e) {
        console.log('Fail fetching logs', e);
      } finally {
        setLoading(false);
      }
    })();

    return apiCall.abort;
  };

  const init = () => fetchLogs(dateRange, levelFilter, postbackIdFilter, clkidFilter);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      init();
    }
  }, []);

  const handleDateRangeChange = async (newDateRange) => {
    try {
      const utcDateRange = await Promise.all(
        newDateRange.map(async (range) => {
          const { finalStartDate, finalEndDate } = await finalDateRange(
            { startDate: range.startDate, endDate: range.endDate },
            'UTC'
          );
          return { ...range, startDate: finalStartDate, endDate: finalEndDate };
        })
      );
      setDateRange(utcDateRange);
    } catch (error) {
      console.error('Error converting date range to UTC:', error);
    }
  };

  const handleDateApply = () => fetchLogs(dateRange, levelFilter, postbackIdFilter, clkidFilter);

  const handleLevelApply = (level) => {
    setLevelFilter(level);
    fetchLogs(dateRange, level, postbackIdFilter, clkidFilter);
  };

  const handlePostbackIdApply = (postbackId) => {
    setPostbackIdFilter(postbackId);
    fetchLogs(dateRange, levelFilter, postbackId, clkidFilter);
  };

  const handleClkidApply = (clkid) => {
    setClkidFilter(clkid);
    fetchLogs(dateRange, levelFilter, postbackIdFilter, clkid);
  };

  const handleDateReset = () => {
    setDateRange(DEFAULT_DATE_RANGE);
    fetchLogs(DEFAULT_DATE_RANGE, levelFilter, postbackIdFilter, clkidFilter);
  };

  const openModal = (title, content) => setModal({ open: true, title, content });
  const closeModal = () => setModal({ open: false, title: '', content: '' });

  const tableCols = generateTableCols(openModal);

  const exportOptions = {
    timestamp: {
      label: 'UTC Date',
      formatter: (value) =>
        value ? moment(value).utc().format('YYYY-MM-DD HH:mm:ss') + ' UTC' : '',
    },
    level: 'Level',
    'meta.id': {
      label: 'Postback ID',
      formatter: (_, row) => row.meta?.id || '',
    },
    clkid: 'Clkid',
    payout: 'Payout',
    'meta.url': {
      label: 'URL',
      formatter: (_, row) =>
        row.meta?.url ? `${window.location.origin}${row.meta.url}` : '',
    },
    message: 'Message',
  };

  const exportFileName = `postback-logs${format(dateRange[0].startDate, 'yyyy-MM-dd')}_${format(dateRange[0].endDate, 'yyyy-MM-dd')}.csv`;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={1}>
        <Typography variant="h6">Postback Logs</Typography>
        <IconButton size="small" color="primary" onClick={init}>
          <RefreshIcon />
        </IconButton>
      </Box>
      <Card>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" px={1} pt={1}>
            <Chip
              size="small"
              label={`${format(dateRange[0].startDate, 'M/d/yyyy')} → ${format(dateRange[0].endDate, 'M/d/yyyy')}`}
              onDelete={handleDateReset}
            />
            {levelFilter && (
              <Chip
                size="small"
                label={`Level: ${levelFilter}`}
                onDelete={() => handleLevelApply('')}
              />
            )}
            {postbackIdFilter && (
              <Chip
                size="small"
                label={`Postback ID: ${postbackIdFilter}`}
                onDelete={() => handlePostbackIdApply('')}
              />
            )}
            {clkidFilter && (
              <Chip
                size="small"
                label={`Click ID: ${clkidFilter}`}
                onDelete={() => handleClkidApply('')}
              />
            )}
          </Box>

          <Box display="flex" alignItems="center" px={1} gap={1}>
            <SimpleToolbar
              allowExport
              rawExportData={finalLogs}
              exportFileName={exportFileName}
              exportOptions={exportOptions}
            />
            <LogsFilter
              dateRange={dateRange}
              onDateApply={handleDateApply}
              onDateRangeChange={handleDateRangeChange}
              levelFilter={levelFilter}
              onLevelApply={handleLevelApply}
              postbackIdFilter={postbackIdFilter}
              onPostbackIdApply={handlePostbackIdApply}
              clkidFilter={clkidFilter}
              onClkidApply={handleClkidApply}
            />
          </Box>

          <Box sx={{ width: '100%', height: '1000px' }} className="ag-theme-alpine">
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress />
              </Box>
            ) : (
              <AgGridReact
                rowData={finalLogs}
                columnDefs={tableCols}
                defaultColDef={{
                  flex: 1,
                  sortable: true,
                  wrapText: true,
                  autoHeight: true,
                  cellStyle: {
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0',
                  },
                }}
                pagination={true}
                paginationPageSize={20}
                enableCellTextSelection={true}
              />
            )}
          </Box>
        </Box>
      </Card>

      <Dialog open={modal.open} onClose={closeModal} maxWidth="md" fullWidth>
        <DialogTitle>{modal.title}</DialogTitle>
        <DialogContent>
          <Typography sx={{ wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
            {modal.content}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const generateTableCols = (openModal) => [
  {
    headerName: 'UTC Date',
    field: 'timestamp',
    flex: 1,
    cellStyle: { justifyContent: 'center' },
    valueGetter: (params) => params.data.timestamp,
    cellRenderer: (params) => {
      const ts = params.value;
      return ts ? moment(ts).utc().format('YYYY-MM-DD HH:mm:ss') + ' UTC' : '';
    },
    comparator: (a, b) => new Date(a) - new Date(b),
  },
  {
    headerName: 'Level',
    field: 'level',
    flex: 1,
    cellStyle: { justifyContent: 'center' },
    cellRenderer: (params) => {
      const level = params.data.level;
      if (level === 'info') return <Chip label="Info" color="info" size="small" />;
      if (level === 'success') return <Chip label="Success" color="success" size="small" />;
      return <Chip label="Error" color="error" size="small" />;
    },
  },
  {
    headerName: 'Postback ID',
    field: 'meta.id',
    flex: 1,
        cellStyle: { justifyContent: 'center' },
    valueGetter: (params) => params.data.meta?.id || '',
  },
  {
    headerName: 'Clkid',
    field: 'clkid',
    flex: 1,
    cellStyle: { justifyContent: 'center' },
    valueGetter: (params) => params.data.clkid || '',
  },
  {
    headerName: 'Payout',
    field: 'payout',
    flex: 1,
    cellStyle: { justifyContent: 'center' },
    valueGetter: (params) => params.data.payout || '',
  },
  {
    headerName: 'URL',
    field: 'meta.url',
    flex: 1,
    cellStyle: { justifyContent: 'center' },
    valueGetter: (params) => params.data.meta?.url || '',
    cellRenderer: (params) => {
      const url = params.data.meta?.url;
      if (!url) return <Typography variant="caption">N/A</Typography>;
      const fullUrl = `${window.location.origin}${url}`;
      return (
        <Chip
          label="Show"
          size="small"
          onClick={() => openModal('URL', fullUrl)}
          clickable
        />
      );
    },
  },
  {
    headerName: 'Message',
    field: 'message',
    flex: 1,
    cellStyle: { justifyContent: 'center' },
    valueGetter: (params) => params.data.message || '',
    cellRenderer: (params) => {
      const msg = params.value;
      if (!msg) return null;
      return (
        <Chip
          label="Show"
          size="small"
          onClick={() => openModal('Message', msg)}
          clickable
        />
      );
    },
  },
];

export default LogsTable;
